import React, { useEffect, useState, useCallback } from "react";
import {
  Activity, Bell, Clock, AlertTriangle, RefreshCw, CheckCircle2, CreditCard,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmtMoney = (n, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));
const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const Stat = ({ icon: Icon, label, value, hint, tone = "neutral", testid }) => {
  const tones = {
    neutral: "text-[#F5F2EE] bg-[#0C0E12] border-white/5",
    good:    "text-emerald-300 bg-emerald-500/5 border-emerald-500/20",
    warn:    "text-amber-300 bg-amber-500/5 border-amber-500/20",
    bad:     "text-rose-300 bg-rose-500/5 border-rose-500/20",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-[#A29F9B]">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="font-display text-3xl">{value}</div>
      {hint && <p className="text-[11px] text-[#A29F9B] mt-1">{hint}</p>}
    </div>
  );
};

/** /admin payments health section — reminders + EuPago pending + Stripe drift.
 *  Auto-refreshes every 30s. Renders inline above the orphan-payments section. */
const PaymentsHealthSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/payments-health");
      setData(r.data);
    } catch (err) {
      // Don't toast on every poll — only on initial load.
      if (!data) {
        toast.error(err?.response?.data?.detail || "Falha ao carregar saúde dos pagamentos.");
      }
    } finally {
      setLoading(false);
    }
  // We intentionally omit `data` from deps — including it would recreate the
  // callback on every refresh and break the 30s polling cadence below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const triggerReminders = async () => {
    setReminding(true);
    try {
      const r = await client.post("/admin/renewal-reminders/run-eupago");
      const f = r.data?.sent_first || 0;
      const u = r.data?.sent_urgent || 0;
      toast.success(`${f} suave(s) + ${u} urgente(s) enviado(s).`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao disparar.");
    } finally {
      setReminding(false);
    }
  };

  if (loading && !data) {
    return (
      <section className="bg-[#161920] border border-white/5 rounded-2xl p-6">
        <p className="text-sm text-[#A29F9B]">A carregar saúde dos pagamentos…</p>
      </section>
    );
  }
  if (!data) return null;

  const stripeDriftTone = data.stripe_drift > 0 ? "bad" : "good";
  const pendingTone = data.eupago_pending.count > 0 ? "warn" : "neutral";

  return (
    <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8"
             data-testid="payments-health">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <span className="label-uppercase inline-flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-[#C5A880]" /> Pagamentos · Saúde
          </span>
          <h2 className="font-display text-2xl md:text-3xl mt-2 text-[#F5F2EE]">
            Estado em tempo real
          </h2>
          <p className="text-xs text-[#A29F9B] mt-1">
            Auto-refresh 30s · última verificação {fmtDateTime(data.checked_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={triggerReminders} disabled={reminding}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#C5A880]/40 bg-[#C5A880]/10 text-[#C5A880] hover:bg-[#C5A880]/20 disabled:opacity-50"
                  data-testid="trigger-reminders">
            {reminding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
            Disparar lembretes agora
          </button>
          <button onClick={load}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-[#C5A880]/40 text-[#A29F9B] hover:text-[#C5A880]"
                  data-testid="payments-health-refresh">
            <RefreshCw className="w-3 h-3" /> Refrescar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat icon={Bell} label="Lembretes T-30" value={data.reminders_24h.first}
              hint="suaves (24h)" testid="health-rem-first" />
        <Stat icon={AlertTriangle} label="Lembretes T-7" value={data.reminders_24h.urgent}
              hint="urgentes (24h)"
              tone={data.reminders_24h.urgent > 0 ? "warn" : "neutral"}
              testid="health-rem-urgent" />
        <Stat icon={Clock} label="A vencer em 7d" value={data.reminders_due_7}
              hint="próximo pass"
              testid="health-due-7" />
        <Stat icon={CreditCard} label="EuPago pagos" value={data.eupago_paid_24h}
              hint="confirmados (24h)"
              tone={data.eupago_paid_24h > 0 ? "good" : "neutral"}
              testid="health-eupago-paid" />
        <Stat icon={AlertTriangle} label="Stripe drift" value={data.stripe_drift}
              hint="expirados >24h"
              tone={stripeDriftTone}
              testid="health-stripe-drift" />
      </div>

      {/* EuPago pending list */}
      <div className={`rounded-xl border p-4 ${data.eupago_pending.count > 0 ? "border-amber-500/30 bg-amber-500/5" : "border-white/5 bg-[#0C0E12]"}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-widest inline-flex items-center gap-2">
            {data.eupago_pending.count > 0 ? (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-amber-300">EuPago — pendentes &gt; 2h</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-emerald-300">EuPago — nada pendente</span>
              </>
            )}
          </span>
          <span className="text-xs text-[#A29F9B]">
            {data.eupago_pending.count} {data.eupago_pending.count === 1 ? "pendente" : "pendentes"}
          </span>
        </div>
        {data.eupago_pending.count > 0 ? (
          <ul className="space-y-1.5" data-testid="health-eupago-list">
            {data.eupago_pending.rows.map((t) => (
              <li key={t.session_id} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-[#A29F9B] whitespace-nowrap">{fmtDateTime(t.created_at)}</span>
                <span className="text-[#F5F2EE] flex-1 truncate">
                  {t.user?.name || "—"} · <span className="text-[#A29F9B]">{t.user?.email || "—"}</span>
                </span>
                <span className="text-[#A29F9B] uppercase text-[10px]">{t.gateway_method || "?"}</span>
                <span className="font-display text-[#F5F2EE] tabular-nums">{fmtMoney(t.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-[#A29F9B] italic">Sem transações pendentes há mais de 2h.</p>
        )}
      </div>
    </section>
  );
};

export default PaymentsHealthSection;
